/**
 * PAR Score Calculator
 * Mirrors the Google Sheet formulas exactly.
 *
 * Inputs:
 *   actives    - # of active listings
 *   pendings   - # of pending listings
 *   reductions - # of actives with a price reduction
 *   solds      - # sold (closed)
 *   faileds    - # that did not sell (expired/withdrawn/cancelled)
 */

export function calcMetric1(actives, pendings) {
  // =IF(OR(pendings=0, actives=""), 10, MAX(10, 100 - ((actives / pendings) * 10)))
  if (!pendings || pendings === 0 || actives === '') return 10;
  return Math.max(10, 100 - (actives / pendings) * 10);
}

export function calcMetric2(actives, reductions) {
  // =MAX(10, 100 - ((reductions / actives) * 30))
  if (!actives || actives === 0) return 10;
  return Math.max(10, 100 - (reductions / actives) * 30);
}

export function calcMetric3(solds, faileds) {
  // =MAX(10, 100 - ((faileds / (solds + faileds)) * 120))
  const total = solds + faileds;
  if (!total || total === 0) return 10;
  return Math.max(10, 100 - (faileds / total) * 120);
}

export function calcPARScore(actives, pendings, reductions, solds, faileds) {
  const m1 = calcMetric1(actives, pendings);
  const m2 = calcMetric2(actives, reductions);
  const m3 = calcMetric3(solds, faileds);
  return Math.round(((m1 + m2 + m3) / 3) * 100) / 100;
}

export function getScoreLabel(score) {
  if (score >= 95) return { label: 'Strong appreciation',    color: '#16a34a', growth: '6%'  };
  if (score >= 85) return { label: 'Moderate appreciation',  color: '#65a30d', growth: '5%'  };
  if (score >= 75) return { label: 'Healthy market',         color: '#84cc16', growth: '4%'  };
  if (score >= 65) return { label: 'Minimal appreciation',   color: '#ca8a04', growth: '2%'  };
  if (score >= 55) return { label: 'Neutral market',         color: '#6b7280', growth: '0%'  };
  if (score >= 45) return { label: 'Moderate depreciation',  color: '#f97316', growth: '-2%' };
  if (score >= 35) return { label: 'Significant depreciation', color: '#ef4444', growth: '-4%' };
  if (score >= 25) return { label: 'Critical depreciation',  color: '#dc2626', growth: '-6%' };
  return           { label: 'Extreme depreciation',          color: '#991b1b', growth: '-8%' };
}

export function calcDerivedValues(actives, pendings, reductions, solds, faileds) {
  const a = parseInt(actives)    || 0;
  const p = parseInt(pendings)   || 0;
  const r = parseInt(reductions) || 0;
  const s = parseInt(solds)      || 0;
  const f = parseInt(faileds)    || 0;

  const previousMarket  = a + s + f;
  const onMarket        = a + p;
  const failurePct      = previousMarket > 0 ? ((f / previousMarket) * 100).toFixed(1) + '%' : '0%';
  const failedTarget    = Math.round(previousMarket * 0.20);
  const reductionTarget = Math.round(a * 0.20);
  const activeMarket    = Math.round(a * 0.50);
  const pctOfSolds      = Math.round(s * 0.80);
  const parScore        = calcPARScore(a, p, r, s, f);
  const scoreInfo       = getScoreLabel(parScore);

  return {
    pm:    previousMarket,
    om:    onMarket,
    fp:    failurePct,
    ft:    failedTarget,
    prt:   reductionTarget,
    at:    activeMarket,
    st:    pctOfSolds,
    fn:    f,
    score: parScore,
    scoreLabel: scoreInfo.label,
    scoreColor: scoreInfo.color,
    scoreGrowth: scoreInfo.growth,
    // raw metrics for display
    metric1: Math.round(calcMetric1(a, p) * 100) / 100,
    metric2: Math.round(calcMetric2(a, r) * 100) / 100,
    metric3: Math.round(calcMetric3(s, f) * 100) / 100,
  };
}
